import * as React from "react";
import { useMutation, useQuery } from "react-query";
import {
  ApiError,
  ApplicationCollection,
  ApplicationsService,
  ApplicationSummary,
  ClientConfig,
  ClientsService,
  SystemSettings,
  SystemSettingsService
} from "gen/clients";
import { DataTable } from "components/DataTable/DataTable";
import { ApplicationId } from "types/ApplicationId";
import { useDialogState } from "hooks/useDialogState";
import { DataTableRowAction } from "components/DataTable/types";
import Button from "@mui/material/Button";
import { Box, Stack, Typography, Alert } from "@mui/material";
import { ApplicationSettingsPanel } from "./ApplicationSettingsPanel/ApplicationSettingsPanel";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { AddApplicationDialog } from "./AddApplicationDialog/AddApplicationDialog";
import { UserAccessDialog } from "./UserAccessDialog/UserAccessDialog";
import { useClientReadOnly } from "contexts/ClientLockContext";

interface Props {
  clientConfig: ClientConfig;
  onSaveSuccess: () => void;
}

const APPS_WITH_SETTINGS: string[] = [
  ApplicationId.reports,
  ApplicationId.machineTranslation,
  ApplicationId.createProject,
  ApplicationId.pbiReports
];

const ApplicationsTab: React.FC<Props> = ({ clientConfig, onSaveSuccess }) => {
  const isReadOnly = useClientReadOnly();
  const [selectedApplicationId, setSelectedApplicationId] = React.useState<string | null>(null);
  const [isSettingsPanelOpen, openSettingsPanel, closeSettingsPanel] = useDialogState();
  const [isUserAccessDialogOpen, openUserAccessDialog, closeUserAccessDialog] = useDialogState();
  const [isDeleteDialogOpen, openDeleteDialog, closeDeleteDialog] = useDialogState();
  const [isAddDialogOpen, openAddDialog, closeAddDialog] = useDialogState();

  const {
    data: applications,
    isLoading: areApplicationsLoading,
    error: applicationsError
  } = useQuery<ApplicationCollection, ApiError>("listApplications", {
    queryFn: ApplicationsService.listApplications,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    cacheTime: 0
  });

  // Fetch system settings to get app bundles
  const {
    data: systemSettings,
    isLoading: areSystemSettingsLoading,
    error: systemSettingsError
  } = useQuery<SystemSettings>(["getSystemSettings"], {
    queryFn: SystemSettingsService.getSystemSettings,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    cacheTime: 0
  });

  const areDataLoading = areApplicationsLoading || areSystemSettingsLoading;

  const { mutate, isLoading, error } = useMutation(ClientsService.updateClientConfig);

  // Get the assigned app bundle
  const assignedAppBundle = React.useMemo(() => {
    if (!clientConfig.appBundleId || !systemSettings?.appBundles) {
      return null;
    }
    return systemSettings.appBundles.find(bundle => bundle.id === clientConfig.appBundleId);
  }, [clientConfig.appBundleId, systemSettings?.appBundles]);

  const clientApplications = React.useMemo(() => {
    if (!applications) {
      return [];
    }

    // If app bundle is assigned, show only app bundle applications
    if (assignedAppBundle) {
      const appBundleApplicationIds = assignedAppBundle.applications?.map(app => app.applicationId) || [];
      return applications.items
        .filter(app => appBundleApplicationIds.includes(app.id))
        .sort((app1, app2) => (app1.name || app1.id).localeCompare(app2.name || app2.id));
    }

    // Otherwise, show client-specific applications (existing behavior)
    return applications.items
      .filter(app => app.isStandard || clientConfig.enabledApplications?.includes(app.id))
      .sort((app1, app2) => (app1.name || app1.id).localeCompare(app2.name || app2.id));
  }, [applications, clientConfig.enabledApplications, assignedAppBundle]);

  const userAccessCell = React.useCallback(
    (app: ApplicationSummary) => {
      const allowedList = clientConfig.appAccessAllowedListByAppId?.[app.id] || [];
      if (allowedList.length === 0) {
        return "All client contacts";
      }
      return (
        <Box>
          {allowedList.map(({ firstName, lastName, email }, index) => (
            <>
              <Typography variant="body2" noWrap={true} title={email} display="inline" key={email}>
                {`${firstName} ${lastName}${index < allowedList.length - 1 ? ", " : ""}`}
              </Typography>{" "}
            </>
          ))}
        </Box>
      );
    },
    [clientConfig.appAccessAllowedListByAppId]
  );

  const onAppSettingsClick = React.useCallback(
    (app: ApplicationSummary) => {
      setSelectedApplicationId(app.id);
      openSettingsPanel();
    },
    [openSettingsPanel]
  );

  const onUserAccessClick = React.useCallback(
    (app: ApplicationSummary) => {
      setSelectedApplicationId(app.id);
      openUserAccessDialog();
    },
    [openUserAccessDialog]
  );

  const onDeleteApplicationClick = React.useCallback(
    (app: ApplicationSummary) => {
      setSelectedApplicationId(app.id);
      openDeleteDialog();
    },
    [openDeleteDialog]
  );

  const onConfirmDelete = React.useCallback(() => {
    const enabledApplications = clientConfig.enabledApplications?.filter(app => app !== selectedApplicationId);
    mutate(
      {
        id: `${clientConfig.id}`,
        requestBody: {
          ...clientConfig,
          enabledApplications
        }
      },
      {
        onSuccess: () => {
          onSaveSuccess();
          closeDeleteDialog();
        }
      }
    );
  }, [clientConfig, closeDeleteDialog, mutate, onSaveSuccess, selectedApplicationId]);

  const selectedApplication = React.useMemo(
    () => applications?.items.find(app => app.id === selectedApplicationId),
    [applications, selectedApplicationId]
  );

  const rowActions = React.useCallback(
    (app: ApplicationSummary): DataTableRowAction[] => [
      ...(APPS_WITH_SETTINGS.includes(app.id)
        ? [
            {
              title: "Settings...",
              action: onAppSettingsClick,
              disabled: !APPS_WITH_SETTINGS.includes(app.id)
            }
          ]
        : []),
      {
        title: "Edit User Access",
        action: onUserAccessClick,
        disabled: isReadOnly
      },
      {
        title: "Delete",
        action: onDeleteApplicationClick,
        disabled: app.isStandard || !!assignedAppBundle || isReadOnly, // Disable delete when app bundle is assigned or locked
        color: "error.main"
      }
    ],
    [onAppSettingsClick, onDeleteApplicationClick, onUserAccessClick, assignedAppBundle, isReadOnly]
  );

  return (
    <>
      {!!error && <SnackbarApiError error={error} />}
      <Stack spacing={2}>
        <Typography variant="h6">Enabled Applications</Typography>

        {/* Show informational alert when app bundle is assigned */}
        {!areDataLoading && assignedAppBundle && (
          <Alert severity="info" sx={{ mb: 2 }}>
            The enabled applications for this client are configured in the app bundle '{assignedAppBundle.name}'.
          </Alert>
        )}

        {/* Only show Add Application button when no app bundle is assigned */}
        {!areDataLoading && !assignedAppBundle && (
          <Box>
            <Button variant="contained" size="large" color="primary" onClick={openAddDialog} disabled={isReadOnly}>
              Add Application
            </Button>
          </Box>
        )}
        <DataTable
          data={clientApplications}
          isLoading={areDataLoading}
          error={applicationsError || systemSettingsError}
          rowKey={(app: ApplicationSummary) => app.id}
          columns={[
            {
              id: "name",
              title: "Name",
              cell: (app: ApplicationSummary) => (
                <Typography variant="body2" noWrap={true}>
                  {app.name || app.id}
                </Typography>
              )
            },
            { id: "description", title: "Description", cell: (app: ApplicationSummary) => app.description },
            ...(assignedAppBundle
              ? []
              : [
                  {
                    id: "isStandard",
                    title: "Standard",
                    cell: (app: ApplicationSummary) => (app.isStandard ? "Yes" : "No")
                  }
                ]),
            { id: "userAccess", title: "Allowed Users", cell: userAccessCell }
          ]}
          rowActions={rowActions}
          dense={true}
        />
      </Stack>
      {isSettingsPanelOpen && selectedApplication && (
        <ApplicationSettingsPanel
          clientConfig={clientConfig}
          application={selectedApplication}
          onClose={closeSettingsPanel}
          onSaveSuccess={onSaveSuccess}
        />
      )}
      {isDeleteDialogOpen && selectedApplication && (
        <ConfirmationDialog
          title="Delete Application"
          content={
            <Box>
              Are you sure you want to delete application{" "}
              <strong>{selectedApplication.name || selectedApplication.id}</strong> from the client? This will make it
              unavailable for the client users.
            </Box>
          }
          onClose={closeDeleteDialog}
          onConfirm={onConfirmDelete}
          isLoading={isLoading}
          confirmationButtonColor="error"
        />
      )}
      {isUserAccessDialogOpen && selectedApplication && selectedApplication && (
        <UserAccessDialog
          application={selectedApplication}
          clientConfig={clientConfig}
          onSaveSuccess={() => {
            onSaveSuccess();
            closeUserAccessDialog();
          }}
          onClose={closeUserAccessDialog}
        />
      )}
      {isAddDialogOpen && applications && !assignedAppBundle && (
        <AddApplicationDialog
          clientConfig={clientConfig}
          availableToAddApplications={applications.items.filter(
            app => !clientApplications.some(clientApp => clientApp.id === app.id)
          )}
          onClose={closeAddDialog}
          onSuccess={() => {
            onSaveSuccess();
            closeAddDialog();
          }}
        />
      )}
    </>
  );
};

export { ApplicationsTab };
